import React, { useEffect, useState } from 'react';
import { Loader2, TrendingUp, PieChart as PieIcon } from 'lucide-react';
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  PieChart, Pie, Cell, Legend, BarChart, Bar
} from 'recharts';
import { adminApi } from '../lib/api';

const formatFCFA = (n) => new Intl.NumberFormat('fr-FR').format(n);

const COLORS_PKG = ['#F4A340', '#E89534', '#0f172a', '#94a3b8', '#059669'];
const COLORS_STATUS = {
  pending: '#F59E0B',
  confirmed: '#10B981',
  completed: '#3B82F6',
  cancelled: '#EF4444'
};
const STATUS_LABEL = { pending: 'En attente', confirmed: 'Confirmée', completed: 'Terminée', cancelled: 'Annulée' };
const PROVIDER_LABEL = { mtn: 'MTN', moov: 'Moov', celtis: 'Celtiis' };
const PROVIDER_COLORS = { mtn: '#FFCC00', moov: '#0066B3', celtis: '#E31E24' };

const CustomTooltip = ({ active, payload, label, valuePrefix = '', valueSuffix = '' }) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="bg-white rounded-xl shadow-lg border border-slate-200 p-3 text-xs">
      <div className="font-bold text-slate-900 mb-1">{label}</div>
      {payload.map((p) => (
        <div key={p.dataKey} className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full" style={{ background: p.color || p.fill }} />
          <span className="text-slate-600">{p.name}:</span>
          <span className="font-semibold">{valuePrefix}{formatFCFA(p.value)}{valueSuffix}</span>
        </div>
      ))}
    </div>
  );
};

const DashboardCharts = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    adminApi.getCharts()
      .then(setData)
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="p-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#F4A340]" /></div>;
  if (!data) return null;

  const monthly = data.monthly || [];
  const packages = (data.packages || []).sort((a, b) => b.count - a.count);
  const statuses = (data.statuses || []).map(s => ({ ...s, label: STATUS_LABEL[s.name] || s.name }));
  const providers = (data.providers || []).map(p => ({ ...p, label: PROVIDER_LABEL[p.name] || p.name }));

  const totalYearRevenue = monthly.reduce((sum, m) => sum + (m.revenue || 0), 0);
  const totalYearCount = monthly.reduce((sum, m) => sum + (m.count || 0), 0);

  return (
    <div className="space-y-6">
      {/* Revenue over time */}
      <div className="bg-white rounded-2xl p-6 border border-slate-200">
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-[#E89534]" />
              <h3 className="text-lg font-bold text-slate-900">Revenus mensuels</h3>
            </div>
            <p className="text-xs text-slate-500 mt-1">Sur les 12 derniers mois · Réservations confirmées</p>
          </div>
          <div className="text-right">
            <div className="text-xs text-slate-500">Total 12 mois</div>
            <div className="text-xl font-bold text-[#E89534]">{formatFCFA(totalYearRevenue)} F</div>
            <div className="text-[10px] text-slate-400">{totalYearCount} réservations</div>
          </div>
        </div>
        <ResponsiveContainer width="100%" height={260}>
          <AreaChart data={monthly} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="colRev" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#F4A340" stopOpacity={0.7} />
                <stop offset="100%" stopColor="#F4A340" stopOpacity={0.05} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
            <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} tickFormatter={(v) => v >= 1000 ? `${v / 1000}k` : v} />
            <Tooltip content={<CustomTooltip valueSuffix=" F" />} cursor={{ stroke: '#F4A340', strokeWidth: 1, strokeDasharray: '4 4' }} />
            <Area type="monotone" dataKey="revenue" name="Revenus" stroke="#F4A340" strokeWidth={2.5} fill="url(#colRev)" />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* 2 columns: packages pie + status bar */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Packages pie */}
        <div className="bg-white rounded-2xl p-6 border border-slate-200">
          <div className="flex items-center gap-2 mb-4">
            <PieIcon className="w-4 h-4 text-[#E89534]" />
            <h3 className="text-lg font-bold text-slate-900">Formules les plus vendues</h3>
          </div>
          {packages.length === 0 ? (
            <div className="text-center py-12 text-slate-400 text-sm">Aucune donnée</div>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie
                  data={packages}
                  dataKey="count"
                  nameKey="name"
                  cx="50%" cy="50%"
                  innerRadius={60} outerRadius={95}
                  paddingAngle={2}
                >
                  {packages.map((_, i) => <Cell key={i} fill={COLORS_PKG[i % COLORS_PKG.length]} />)}
                </Pie>
                <Tooltip formatter={(v, n, p) => [`${v} ventes · ${formatFCFA(p.payload.revenue)} F`, p.payload.name]} />
                <Legend verticalAlign="bottom" iconType="circle" wrapperStyle={{ fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Statuses bar */}
        <div className="bg-white rounded-2xl p-6 border border-slate-200">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-4 h-4 rounded bg-gradient-to-r from-amber-400 via-emerald-500 to-red-400" />
            <h3 className="text-lg font-bold text-slate-900">Réservations par statut</h3>
          </div>
          {statuses.length === 0 ? (
            <div className="text-center py-12 text-slate-400 text-sm">Aucune donnée</div>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={statuses} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} allowDecimals={false} />
                <Tooltip cursor={{ fill: '#faf7f2' }} />
                <Bar dataKey="count" name="Nombre" radius={[10, 10, 0, 0]}>
                  {statuses.map((s, i) => <Cell key={i} fill={COLORS_STATUS[s.name] || '#94a3b8'} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Payment providers */}
      {providers.length > 0 && (
        <div className="bg-white rounded-2xl p-6 border border-slate-200">
          <h3 className="text-lg font-bold text-slate-900 mb-4">Répartition Mobile Money</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {providers.map((p) => {
              const total = providers.reduce((s, x) => s + x.count, 0);
              const pct = total ? Math.round((p.count / total) * 100) : 0;
              return (
                <div key={p.name} className="p-4 rounded-xl border border-slate-100">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-8 h-8 rounded-lg font-bold text-xs flex items-center justify-center text-white" style={{ background: PROVIDER_COLORS[p.name] || '#64748b' }}>
                      {p.name.toUpperCase()}
                    </div>
                    <div className="text-sm font-semibold">{p.label}</div>
                  </div>
                  <div className="text-2xl font-bold text-slate-900">{p.count}</div>
                  <div className="w-full h-2 bg-slate-100 rounded-full mt-2 overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${pct}%`, background: PROVIDER_COLORS[p.name] || '#64748b' }} />
                  </div>
                  <div className="text-xs text-slate-500 mt-1">{pct}% des paiements</div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardCharts;
